import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Copy, Send, Loader2, KeyRound, AlertTriangle } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";

import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import GerenciadorDeTokens from "./GerenciadorDeTokens";

// Bloco de código com botão de copiar. Sem realce de sintaxe de propósito:
// a cor não ajuda a entender um curl, e traria uma dependência a mais.
const Codigo = ({ children, onCopy }) => (
	<div className="relative">
		<pre className="overflow-x-auto rounded-lg border bg-muted p-3 text-xs leading-relaxed">
			<code>{children}</code>
		</pre>
		<Button
			variant="ghost"
			size="icon"
			className="absolute right-1.5 top-1.5 h-7 w-7"
			aria-label="Copiar"
			title="Copiar"
			onClick={onCopy}
		>
			<Copy className="h-3.5 w-3.5" />
		</Button>
	</div>
);

const AbaApiRest = () => {
	// O token vem colado pela pessoa, não do servidor.
	//
	// O sistema guarda apenas o resumo SHA-256 de cada token: ele não sabe o
	// valor, e é isso que impede um vazamento do banco virar acesso à API de
	// todos os clientes. Guardamos no `sessionStorage` para não obrigar a colar
	// de novo a cada teste, e não no `localStorage` porque credencial não deve
	// sobreviver ao fechar a aba.
	const [token, setToken] = useState(
		() => sessionStorage.getItem("tokenDeTeste") || "",
	);
	const [whatsApps, setWhatsApps] = useState([]);

	const [teste, setTeste] = useState({ number: "", body: "", whatsappId: "" });
	const [enviando, setEnviando] = useState(false);

	const base = String(getBackendUrl() || "").replace(/\/+$/, "");
	const endpoint = `${base}/api/messages/send`;

	const carregar = useCallback(async () => {
		try {
			const { data } = await api.get("/whatsapp/");
			setWhatsApps(data || []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const copiar = async (texto, mensagem) => {
		try {
			await navigator.clipboard.writeText(texto);
			toast.success(mensagem);
		} catch {
			toast.error("Não consegui copiar. Selecione o texto e copie na mão.");
		}
	};

	const exemploCurl = `curl -X POST ${endpoint} \\
  -H "Authorization: Bearer ${token || "SEU_TOKEN"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "5511999999999",
    "body": "Olá! Sua encomenda saiu para entrega.",
    "whatsappId": ${whatsApps[0]?.id ?? 1}
  }'`;

	const exemploMidia = `curl -X POST ${endpoint} \\
  -H "Authorization: Bearer ${token || "SEU_TOKEN"}" \\
  -F "number=5511999999999" \\
  -F "body=Segue o comprovante" \\
  -F "medias=@/caminho/do/arquivo.pdf"`;

	const enviarTeste = async (e) => {
		e.preventDefault();

		if (!token) {
			toast.error("Cole um token acima. Gere um no bloco de tokens.");
			return;
		}

		sessionStorage.setItem("tokenDeTeste", token);

		setEnviando(true);
		try {
			// Chamada igual à de fora: token no header, sem o cookie da sessão.
			// Testar por dentro do app esconderia justamente o que costuma falhar.
			const resposta = await fetch(endpoint, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					number: teste.number.replace(/\D/g, ""),
					body: teste.body,
					...(teste.whatsappId && { whatsappId: +teste.whatsappId }),
				}),
			});

			if (resposta.ok) {
				toast.success("Enviada. Confira no WhatsApp do número de destino.");
			} else {
				// O corpo vem como {"error":"CHAVE"}. Mostrar o JSON cru deixava a
				// pessoa diante de `{"error":"ERR_WAPP_CHECK_CONTACT"}` sem saber se
				// errou o número ou se a conexão estava fora; a chave tem tradução.
				const cru = await resposta.text();
				let motivo = cru.slice(0, 200);

				try {
					const chave = JSON.parse(cru)?.error;
					if (chave) {
						const traduzido = i18n.t(`backendErrors.${chave}`);
						motivo = traduzido.startsWith("backendErrors.")
							? chave
							: traduzido;
					}
				} catch {
					// Resposta que não é JSON (erro do proxy, por exemplo): vale o texto.
				}

				toast.error(`A API recusou (${resposta.status}): ${motivo}`);
			}
		} catch (err) {
			toast.error(`Não consegui chamar a API: ${err.message}`);
		}
		setEnviando(false);
	};

	return (
		<div className="space-y-6">
				<p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Para outro sistema seu mandar mensagem por aqui — um e-commerce
					avisando que o pedido saiu, um ERP cobrando um boleto. Quem recebe vê
					uma conversa normal, e ela entra na fila de atendimento como qualquer
					outra.
				</p>

				<GerenciadorDeTokens />

				{/* Contrato */}
				<div className="space-y-4 rounded-xl border bg-card p-4">
					<h2 className="text-sm font-semibold">Como chamar</h2>

					<div className="overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
									<th className="py-2 pr-4">Campo</th>
									<th className="py-2 pr-4">Obrigatório</th>
									<th className="py-2">O que é</th>
								</tr>
							</thead>
							<tbody className="[&>tr]:border-b [&>tr:last-child]:border-0">
								<tr>
									<td className="py-2 pr-4 font-mono text-xs">number</td>
									<td className="py-2 pr-4">Sim</td>
									<td className="py-2 text-muted-foreground">
										Só dígitos, com país e DDD. Ex.: 5511999999999
									</td>
								</tr>
								<tr>
									<td className="py-2 pr-4 font-mono text-xs">body</td>
									<td className="py-2 pr-4">Sim</td>
									<td className="py-2 text-muted-foreground">
										O texto da mensagem. Com arquivo, vira a legenda.
									</td>
								</tr>
								<tr>
									<td className="py-2 pr-4 font-mono text-xs">whatsappId</td>
									<td className="py-2 pr-4">Não</td>
									<td className="py-2 text-muted-foreground">
										De qual conexão sai. Sem ele, sai pela conexão padrão.
									</td>
								</tr>
								<tr>
									<td className="py-2 pr-4 font-mono text-xs">medias</td>
									<td className="py-2 pr-4">Não</td>
									<td className="py-2 text-muted-foreground">
										Arquivos, via multipart/form-data. Aceita mais de um.
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<div>
						<Label className="text-xs text-muted-foreground">
							Texto simples
						</Label>
						<div className="mt-1">
							<Codigo onCopy={() => copiar(exemploCurl, "Exemplo copiado.")}>
								{exemploCurl}
							</Codigo>
						</div>
					</div>

					<div>
						<Label className="text-xs text-muted-foreground">Com arquivo</Label>
						<div className="mt-1">
							<Codigo onCopy={() => copiar(exemploMidia, "Exemplo copiado.")}>
								{exemploMidia}
							</Codigo>
						</div>
					</div>

					<p className="text-xs leading-relaxed text-muted-foreground">
						Resposta vazia com status 200 significa aceita. O número é conferido
						no WhatsApp antes do envio: se não existir, a chamada falha em vez
						de sumir em silêncio.
					</p>
				</div>

				{/* Testador */}
				<form
					onSubmit={enviarTeste}
					className="space-y-3 rounded-xl border bg-card p-4"
				>
					<h2 className="text-sm font-semibold">Testar agora</h2>
					<p className="text-xs leading-relaxed text-muted-foreground">
						Manda de verdade, pelo mesmo caminho que o seu sistema vai usar.
						Escolha um número seu.
					</p>

					<div>
						<Label htmlFor="token-teste">Token</Label>
						<Input
							id="token-teste"
							type="password"
							autoComplete="off"
							placeholder="Cole aqui o token que você gerou"
							className="font-mono text-xs"
							value={token}
							onChange={(e) => setToken(e.target.value)}
						/>
						<p className="mt-1 text-xs text-muted-foreground">
							O sistema não guarda o valor do token, só o resumo dele. Por isso
							ele precisa ser colado aqui — some ao fechar a aba.
						</p>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						<div>
							<Label htmlFor="number">Número de destino</Label>
							<Input
								id="number"
								required
								placeholder="5511999999999"
								value={teste.number}
								onChange={(e) =>
									setTeste({ ...teste, number: e.target.value })
								}
							/>
						</div>
						<div>
							<Label htmlFor="whatsappId">Conexão</Label>
							<Select
								value={teste.whatsappId || "padrao"}
								onValueChange={(v) =>
									setTeste({ ...teste, whatsappId: v === "padrao" ? "" : v })
								}
							>
								<SelectTrigger id="whatsappId">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="padrao">Conexão padrão</SelectItem>
									{whatsApps.map((w) => (
										<SelectItem key={w.id} value={String(w.id)}>
											{w.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>

					<div>
						<Label htmlFor="body">Mensagem</Label>
						<Textarea
							id="body"
							required
							rows={3}
							value={teste.body}
							onChange={(e) => setTeste({ ...teste, body: e.target.value })}
						/>
					</div>

					<Button type="submit" disabled={enviando || !token}>
						{enviando ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							<Send className="h-4 w-4" />
						)}
						Enviar de verdade
					</Button>
				</form>
		</div>
	);
};

export default AbaApiRest;
